import { View, RefreshControl, ScrollView } from 'react-native';
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Toast from 'react-native-root-toast';

import { nativePlanogramVersions } from '../../../common/utils/vending-settings';
import { POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';
import { useBooleanState, useEventCallback } from '../../../common/utils/hooks';
import { useUserContext } from '../../../common/utils/context';
import { MARK_SERVICE_DOCKET_PICK_PARTIALLY_COMPLETE } from '../../../common/graphql/serviceDocket';

import { Heading, Dialog, Text } from '../../components';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useRefetchOnFocus } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { sortPickListItems } from '../../utils/sortMethods';
import { DetailContainer } from '../../containers';
import { useTheme } from '../../theme';
import {
	Card,
	PrimaryButton,
	Screen,
	SecondaryButton,
} from '../../design/components';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { PlanogramDetail } from '../planogram';
import PlanogramSettingsButton from '../planogram/PlanogramSettingsButton';
import useMyDayMode, { MY_DAY_MODE } from '../myDay/useMyDayMode';
import PickListItemEdit from './PickListItemEdit';
import PickListDocketTags from './PickListDocketTags';
import PickListStartCard from './PickListStartCard';
import WarehousePrintJobStatus from './WarehousePrintJobStatus';
import useWarehousePrintJob from './useWarehousePrintJob';
import { createPickListDetailStyles } from './PickListDetail.styles';
import { createMappedItems, toNonNegativeNumber } from './PickListDetailHelpers';
import usePickListItemActions from './usePickListItemActions';
import usePickListTubWeight from './usePickListTubWeight';
import usePickListTubWeightExclusions from './usePickListTubWeightExclusions';
import usePickListVoiceMode from './usePickListVoiceMode';
import PickListTubSwitchOverlay from './PickListTubSwitchOverlay';
import PickListTubWeightDock from './PickListTubWeightDock';
import PickListTubWeightExclusionEditDialog from './PickListTubWeightExclusionEditDialog';
import PickListTubWeightExclusionsDialog from './PickListTubWeightExclusionsDialog';
import PickListVoiceOverlay from './PickListVoiceOverlay';

const PickListDetail = ({
	data: pickList,
	machineName,
	refetch,
	recalculateMachinePicklist,
	loadingRecalculation,
	loadingSetQuantitiesToZero,
	startLabelPrinted,
	allowPartialCompletion,
	disableActions = false,
	readOnly = false,
	trainingMode = false,
	backLabel,
	banner,
	headerMenu,
}) => {

	const navigation = useNavigation();
	const { user, hasPermission, isPicker } = useUserContext();
	const { mode: myDayMode } = useMyDayMode();
	// Once the stop is restocked (DEX or manual restock) the stock is in the machine, so the pick list is view only.
	const isReadOnly = readOnly || Boolean(pickList.serviceDocket?.isRestocked);
	const canEditPickList = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS) && !disableActions && !isReadOnly;

	const {
		settings: {
			pickListSort,
			picklistShowAllSelections,
			picklistBatchSameProduct,
			picklistHideImages,
			picklistHideHealthCompliance,
			picklistHidePickedItems,
			picklistShowSalesLastXDays,
			picklistShowPickOrder,
			picklistShowQuantitySoldAfterRecalc = true, // Default to true if not set
			picklistShowExpiryDates = false, // Default to false if not set
		},
		tenantSettings: {
			enableTubsFieldOnDockets,
			useExpiryDates,
		},
	} = user;
	const isFirstServiceAtLocation = pickList?.machine?.firstService === null;
	const defaultShowAllSelections = isFirstServiceAtLocation ? true : picklistShowAllSelections;

	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const [displayedItems, setDisplayedItems] = useState(null);
	const [refreshItems, setRefreshItems] = useState(false);
	const scrollViewRef = useRef();
	const [lastPickedProductAction, setLastPickedProductAction] = useState(null);
	const [isStartCardDismissed, setIsStartCardDismissed] = useState(Boolean(startLabelPrinted));
	const [markPickPartiallyComplete, { loading: isFinishingForNow }] = useMutation(MARK_SERVICE_DOCKET_PICK_PARTIALLY_COMPLETE);
	const tubLabel = useWarehousePrintJob({
		serviceDocketId: pickList.serviceDocket?.id,
		labelType: 'TUB',
		enabled: !disableActions,
		disableOffline: trainingMode,
	});
	const packLabel = useWarehousePrintJob({
		serviceDocketId: pickList.serviceDocket?.id,
		labelType: 'PACK',
		enabled: !disableActions,
		disableOffline: trainingMode,
	});

	// The first picking step is printing the tub sticker (driver, date, machine),
	// so the picked stock ends up in a labelled tub for the right run.
	const hasAnyPickedItem = useMemo(() => (
		(pickList.pickListItems || []).some(item => item.isPicked || item.extraQuantityIsPicked)
	), [pickList.pickListItems]);
	const shouldShowStartCard = !isReadOnly && !hasAnyPickedItem && !isStartCardDismissed;

	const handlePrintStartLabel = useEventCallback(tubLabel.requestLabel);
	const handlePrintPackLabel = useEventCallback(packLabel.requestLabel);

	useEffect(() => {
		if (tubLabel.job?.status === 'COMPLETED') {
			setIsStartCardDismissed(true);
		}
	}, [tubLabel.job?.status]);

	// Map the pick list items, which is an unusual shape, into essentially standard planogram items, plus
	// required quantity. This puts it in the same shape as when setting inventories, etc.
	const [mappedPickInventory, setMappedPickInventory] = useState(createMappedItems(pickList));
	const isPickListFullyPicked = useMemo(() => mappedPickInventory.every(item => (
		!toNonNegativeNumber(item.requiredQuantity) || Boolean(item.isPicked)
	)), [mappedPickInventory]);
	const canFinishForNow = Boolean(allowPartialCompletion &&
		canEditPickList &&
		pickList.serviceDocket?.id &&
		!pickList.serviceDocket?.isRestocked &&
		!isPickListFullyPicked);

	const finishForNow = useEventCallback(async () => {
		try {
			await markPickPartiallyComplete({
				variables: {
					serviceDocketId: pickList.serviceDocket.id,
					...(trainingMode ? { adminTraining: true } : {}),
				},
			});
			navigation.goBack();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to finish this pick for now');
		}
	});

	const {
		loadingTubWeightExclusions,
		isTubWeightExclusionsDialogOpen,
		setIsTubWeightExclusionsDialogOpen,
		tubWeightExclusionEditQuantity,
		setTubWeightExclusionEditQuantity,
		tubWeightProductExclusionSummaries,
		tubWeightExcludedSummary,
		tubWeightExclusionEditSummary,
		setTubWeightExcludedQuantityForProduct,
		openTubWeightExclusionEditor,
		closeTubWeightExclusionEditor,
		applyTubWeightExclusionEdit,
		applyQuickExcludeLastPicked,
		lastPickedProductSummary,
		openLastPickedTubWeightEditor,
	} = usePickListTubWeightExclusions({
		mappedPickInventory,
		setMappedPickInventory,
		canEditPickList,
		lastPickedProductAction,
	});

	const {
		tubWeightDisplay,
		pendingTubSwitchIndex,
		highestAcknowledgedTubIndexRef,
		acknowledgeTubSwitch,
	} = usePickListTubWeight({ mappedPickInventory, tenantSettings: user?.tenantSettings });

	// Update mapped inventory whenever pickList changes
	useEffect(() => {
		setMappedPickInventory(createMappedItems(pickList));
	}, [pickList]);

	useEffect(() => {
		if (refreshItems) {
			setMappedPickInventory(createMappedItems(pickList));
			setRefreshItems(false);
		}
	}, [pickList, loadingSetQuantitiesToZero, loadingRecalculation, refreshItems]);

	useEffect(() => {
		// when finished loading, refresh the pick list
		if (!loadingSetQuantitiesToZero && !loadingRecalculation) {
			setRefreshItems(true);
		}
	}, [loadingSetQuantitiesToZero, loadingRecalculation]);

	const {
		pickListItemToggleIsPicked,
		setPickListItemIsPickedBulk,
		setPickListItemQuantity,
		setPickListItemExpiryDates,
		handleAddMissingQuantity,
		loadingToggleIsPicked,
		loadingSetPickedBulk,
		loadingModifyPickListItemsQuantity,
	} = usePickListItemActions({
		pickList,
		mappedPickInventory,
		setMappedPickInventory,
		canEditPickList,
		setLastPickedProductAction,
		setRefreshItems,
	});

	const serviceRunPlanned = pickList?.serviceRun?.planned;
	const serviceFrequencyDays = pickList?.machine?.averageDaysSinceLastService;

	const {
		isVoiceMode,
		currentVoiceItemIndex,
		startVoiceMode,
		onPressNext,
		repeatVoiceItem,
		pauseVoiceMode,
		quitVoiceMode,
	} = usePickListVoiceMode({
		displayedItems,
		scrollViewRef,
		pickListItemToggleIsPicked,
		setPickListItemIsPickedBulk,
		tubWeightDisplay,
		pendingTubSwitchIndex,
		highestAcknowledgedTubIndexRef,
	});

	const totalPickQuantity = useMemo(() => mappedPickInventory.reduce((acc, item) => acc + item.requiredQuantity, 0), [mappedPickInventory]);

	const machineUsesIngredients = Boolean(pickList.machine.ingredientPlanogram);
	const isMarket = pickList.machine.type === POS_TYPE.MARKET;

	const [selectedItemsIds, setSelectedItemsIds] = useState([]);

	const clearEditedSelection = useCallback(() => setSelectedItemsIds([]), [setSelectedItemsIds]);

	const onClickPlanogramItem = useCallback(id => () => {
		const ids = Array.isArray(id) ? id : [id];
		setSelectedItemsIds(ids);
	}, [setSelectedItemsIds]);

	const isUpdatingPickList = loadingSetQuantitiesToZero ||
		loadingRecalculation ||
		loadingTubWeightExclusions ||
		loadingToggleIsPicked ||
		loadingSetPickedBulk ||
		loadingModifyPickListItemsQuantity;
	useRefetchOnFocus(refetch, { enabled: !selectedItemsIds.length && !isUpdatingPickList });

	const styles = useThemedStyles(createPickListDetailStyles);
	const { theme: { ui: { layout } } } = useTheme();
	const insets = useSafeAreaInsets();
	// Voice mode and the tub change prompt cover everything below the header.
	const overlayTopStyle = useMemo(() => ({ top: insets.top + layout.headerHeight }), [insets.top, layout.headerHeight]);
	const [isSettingsDialogOpen, toggleSettingsDialog] = useBooleanState(false);

	// Drivers only see packing tags they act on; warehouse-only (label print
	// only) tags stay for pickers (picker role or picker roster shift, as in
	// My day) and planners. Without My day, the picker role decides.
	const isPickerMode = myDayMode === MY_DAY_MODE.PICKER ||
		(myDayMode === MY_DAY_MODE.CLASSIC && isPicker);
	const isDriverView = !isPickerMode &&
		pickList.serviceRun?.driver?.id === user.id &&
		pickList.serviceDocket?.picker?.id !== user.id;
	const visiblePackTags = useMemo(() => (
		(pickList.serviceDocket?.packTags || []).filter(tag => !isDriverView || !tag.labelPrintOnly)
	), [isDriverView, pickList.serviceDocket?.packTags]);

	// The tub weight card is a packing aid, so a read-only list leaves it out.
	const tubWeightDock = tubWeightDisplay && !isReadOnly ? (
		<PickListTubWeightDock
			tubWeightDisplay={tubWeightDisplay}
			tubWeightExcludedSummary={tubWeightExcludedSummary}
			lastPickedProductAction={lastPickedProductAction}
			lastPickedProductSummary={lastPickedProductSummary}
			hasExclusionSummaries={Boolean(tubWeightProductExclusionSummaries.length)}
			loadingTubWeightExclusions={loadingTubWeightExclusions}
			onExcludeLast={applyQuickExcludeLastPicked}
			onEditLastExclusion={openLastPickedTubWeightEditor}
			onOpenExclusions={() => setIsTubWeightExclusionsDialogOpen(true)}
		/>
	) : null;

	const headerRight = (
		<View style={styles.headerActions}>
			<ServiceNoteButton />
			<PlanogramSettingsButton onPress={toggleSettingsDialog} accessibilityLabel="Pick list settings" />
			{!isReadOnly && headerMenu}
		</View>
	);

	return (
		<View style={styles.page}>
			<Screen
				scroll={false}
				backLabel={backLabel}
				banner={banner}
				headerRight={headerRight}
				dock={tubWeightDock}
			>
				<ScrollView
					style={styles.scrollView}
					scrollIndicatorInsets={{ right: 1 }}
					ref={scrollViewRef}
					refreshControl={(
						<RefreshControl
							refreshing={isRefreshing}
							onRefresh={() => {
								setRefreshItems(true);
								refreshPage();
							}}
						/>
					)}
				>
					<View style={styles.top}>
						<Heading
							heading="Pick list"
							subHeading={machineName}
						/>
						{
							shouldShowStartCard && (
								<PickListStartCard
									onPrint={handlePrintStartLabel}
									isSubmitting={tubLabel.isSubmitting}
									disabled={disableActions}
									onSkip={() => setIsStartCardDismissed(true)}
								/>
							)
						}
						<WarehousePrintJobStatus
							job={tubLabel.job}
							labelName="Tub sticker"
							onReprint={tubLabel.requestReprint}
							isSubmitting={tubLabel.isSubmitting}
						/>
						<PickListDocketTags
							serviceDocket={pickList.serviceDocket}
							enableTubsFieldOnDockets={enableTubsFieldOnDockets}
							packTags={visiblePackTags}
						/>
						{!isReadOnly && (
							<View style={styles.buttonRow}>
								<SecondaryButton
									style={styles.flexButton}
									variant="tonal"
									label="Recalculate"
									icon="refresh-cw"
									disabled={!canEditPickList || pickList.serviceDocket?.isRestocked}
									onPress={async () => {
										await recalculateMachinePicklist();
									}}
									loading={loadingRecalculation}
								/>
								<PrimaryButton
									style={styles.flexButton}
									size="compact"
									label="Voice mode"
									icon="volume-2"
									onPress={startVoiceMode}
									disabled={!canEditPickList}
								/>
							</View>
						)}
					</View>
					<PlanogramDetail
						pickListItemToggleIsPicked={canEditPickList ? pickListItemToggleIsPicked : undefined}
						setPickListItemIsPickedBulk={canEditPickList ? setPickListItemIsPickedBulk : undefined}
						items={mappedPickInventory}
						totalPickQuantity={totalPickQuantity}
						version={nativePlanogramVersions.PICKLIST_VIEW}
						onClickItem={canEditPickList ? onClickPlanogramItem : undefined}
						onAddMissingQuantity={canEditPickList ? handleAddMissingQuantity : undefined}
						itemClickPermission={canEditPickList ? ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS : undefined}
						// Default planogram filters
						defaultSortMethod={pickListSort}
						sortFunction={sortPickListItems}
						defaultShowAllSelections={defaultShowAllSelections}
						defaultBatchSameProduct={picklistBatchSameProduct}
						defaultHideImages={picklistHideImages}
						defaultHideHealthCompliance={picklistHideHealthCompliance}
						defaultHidePickedItems={picklistHidePickedItems}
						defaultShowSalesLastXDaysForPicklists={picklistShowSalesLastXDays}
						defaultShowPickOrderForPicklists={picklistShowPickOrder}
						defaultShowQuantitySoldAfterRecalc={picklistShowQuantitySoldAfterRecalc}
						defaultShowExpiryDates={picklistShowExpiryDates}
						machineUsesIngredients={machineUsesIngredients}
						setDisplayedItems={setDisplayedItems}
						isMarket={isMarket}
						serviceRunPlanned={serviceRunPlanned}
						serviceFrequencyDays={serviceFrequencyDays}
						planogramItemGroups={pickList.machine.planogram.planogramItemGroups}
						isSettingsDialogOpen={isSettingsDialogOpen}
						toggleSettingsDialog={toggleSettingsDialog}
					/>
					<View style={styles.bottom}>
						{!isReadOnly && (
							<SecondaryButton
								label="Print pack label"
								icon="printer"
								onPress={handlePrintPackLabel}
								loading={packLabel.isSubmitting}
								disabled={disableActions}
							/>
						)}
						<WarehousePrintJobStatus
							job={packLabel.job}
							labelName="Pack label"
							onReprint={packLabel.requestReprint}
							isSubmitting={packLabel.isSubmitting}
						/>
						{
							canFinishForNow && (
								<Card style={styles.card}>
									<Text style={styles.cardTitle}>
										Packed all you can?
									</Text>
									<Text style={styles.cardText}>
										Leave unavailable items unticked. Finish for now counts this POS as completed in Picker My Day, while drivers and planners still see that the pick is outstanding.
									</Text>
									<PrimaryButton
										size="compact"
										label="Finish for now"
										onPress={finishForNow}
										loading={isFinishingForNow}
									/>
								</Card>
							)
						}
					</View>
				</ScrollView>
			</Screen>
			{ isVoiceMode && (
				<PickListVoiceOverlay
					banner={banner}
					overlayTopStyle={overlayTopStyle}
					currentVoiceItemIndex={currentVoiceItemIndex}
					onNext={onPressNext}
					onRepeat={repeatVoiceItem}
					onPause={pauseVoiceMode}
					onQuit={quitVoiceMode}
				/>
			)}
			{
				pendingTubSwitchIndex !== null && (
					<PickListTubSwitchOverlay
						banner={banner}
						overlayTopStyle={overlayTopStyle}
						tubIndex={pendingTubSwitchIndex}
						onAcknowledge={acknowledgeTubSwitch}
					/>
				)
			}

			<PickListTubWeightExclusionsDialog
				isOpen={isTubWeightExclusionsDialogOpen}
				onClose={() => setIsTubWeightExclusionsDialogOpen(false)}
				summaries={tubWeightProductExclusionSummaries}
				loading={loadingTubWeightExclusions}
				onSetExcludedQuantity={setTubWeightExcludedQuantityForProduct}
				onEdit={openTubWeightExclusionEditor}
			/>

			<PickListTubWeightExclusionEditDialog
				summary={tubWeightExclusionEditSummary}
				quantity={tubWeightExclusionEditQuantity}
				setQuantity={setTubWeightExclusionEditQuantity}
				loading={loadingTubWeightExclusions}
				onClose={closeTubWeightExclusionEditor}
				onSave={applyTubWeightExclusionEdit}
			/>

			<Dialog
				isOpen={selectedItemsIds.length > 0}
				onClose={clearEditedSelection}
			>
				<PickListItemEdit
					pickListItems={mappedPickInventory.filter(i => selectedItemsIds.includes(i.id))}
					onButtonPress={setPickListItemQuantity}
					onSaveExpiryDates={setPickListItemExpiryDates}
					quantityFieldName="requiredQuantity"
					isMarket={isMarket}
					allowExpiryDateEditing={useExpiryDates}
					defaultShowSalesLastXDaysForPicklists={picklistShowSalesLastXDays}
				/>
			</Dialog>
		</View>
	);
};

export default DetailContainer(PickListDetail);
